// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — Canonical Emergency-Eligibility Brain (2026-08-05)
 * ========================================================
 * ONE source of truth for "may an emergency booking squeeze around THIS client's
 * service?" and "is THIS barber allowed to accept emergency bookings?". Pure logic —
 * NO React, NO Firebase. Every surface (POS, customer-portal, public-booking, the
 * squeeze finder, the emergency creation flow) must read from here so the rules can
 * never drift between apps.
 *
 * This is the twin of shared/availability.ts (the scheduling brain). Availability answers
 * "is a slot free / within schedule"; THIS module answers "is an emergency squeeze
 * *permitted* there" — a separate permission layer on top of raw availability.
 *
 * DO NOT EDIT the per-app copies in <app>/src/shared/ — edit THIS file at repo root
 * and run scripts/sync-shared.sh to propagate.
 *
 * ── DECISIONS ENCODED (Jose, 2026-08-05) ──────────────────────────────────────────
 * BARBER SIDE IS THE MASTER GATE:
 *   NO emergency squeezing happens at all unless the barber has emergency access toggled
 *   ON (see barber gate below). If the barber isn't active, none of the client rules apply.
 *
 * CLIENT SIDE — DEFAULT ALLOW, TOGGLE TO PROTECT (blocklist):
 *   Every service ALLOWS emergencies to be squeezed around a client's appointments BY
 *   DEFAULT. You toggle SPECIFIC services OFF ("do not squeeze emergencies around this
 *   service") to protect just those. Absence of an override = allowed. A per-service
 *   protect override, a global blockSqueezing flag, or an appointment-level
 *   noEmergencySqueeze makes that service/appointment protected.
 *
 * BARBER SIDE — PERFORMANCE GATES, ADMIN GRANTS:
 *   A barber does not get emergency access automatically. Flow:
 *     1. Barber REQUESTS access from their profile (emergencyBooking.requested).
 *     2. Their BPI (Barber Performance Index) must MEET the shop threshold to be eligible.
 *     3. Admin flips the MASTER SWITCH (emergencyBooking.enabled) — only grantable when
 *        the barber is eligible (or an explicit manual override).
 *   Runtime "can this barber take an emergency" = master switch ON.
 *
 * BPI is a COMPOSITE score (0-100) over multiple parameters — performance, customer
 * service, timeliness, retention, "BPIX", … The full parameter list + weights are still
 * being finalized by Jose; the framework below is EXTENSIBLE — add parameters to
 * BPI_PARAMETERS and computeBarberBPI() picks them up. Threshold comes from shop settings
 * (bookingSettings.emergencyBooking.barberBPIAutoUnlock, default 85).
 */

// The barber emergency PRIVILEGE is gated on the Professional Practice Index — see shared/ppi.ts.
import { emergencyPrivilegeCheck, computePPI, PROFICIENT_MIN } from './ppi';
import type { PpiScores, PrivilegeRequirement } from './ppi';

// ── Minimal shapes (each app feeds its own records in) ──────────────────────────────

/** Per-service emergency override as stored on a client (array is canonical; object legacy).
 *  Default (no override) = ALLOWED. An override with allowEmergencySqueeze:false PROTECTS
 *  that service (the toggled-off case). */
export interface EmergencyServiceOverride {
  serviceVariationId?: string;
  serviceName?: string;
  allowEmergencySqueeze: boolean;   // false = PROTECTED (toggled off). Absence = allowed (default).
}

export interface EmergencyClient {
  emergencyServiceOverrides?: EmergencyServiceOverride[] | Record<string, boolean> | null;
  emergencyBookingSettings?: { blockSqueezing?: boolean } | null;   // global "never squeeze me"
}

export interface EmergencyAppointment {
  service?: string;
  noEmergencySqueeze?: boolean;     // appointment-level hard protect
}

/** Barber fields the emergency gate reads. The privilege is gated on the PPI scorecard
 *  (shared/ppi.ts) — specific pillars, not a single revenue number. */
export interface EmergencyBarber {
  emergencyBooking?: {
    enabled?: boolean;                                   // MASTER SWITCH (admin-granted)
    requested?: boolean;                                 // barber asked for access
    requestedAt?: any;
    enabledAt?: any;
    enabledBy?: string;
    unlockReason?: 'ppi_privilege' | 'manual_grant' | string;
    manualOverride?: boolean;                            // admin grant that bypasses the PPI gate
  } | null;
  ppiScores?: PpiScores | null;                          // coach-entered Professional Practice scores
}

// ── THE fixed emergency service ─────────────────────────────────────────────────────

/**
 * An emergency booking is ALWAYS one service — a dedicated "Emergency Haircut" at $50 (Jose
 * 2026-08-06). This is NOT a catalog service; it's a fixed emergency-only service so the POS tile
 * and the customer portal label + charge identically. It replaces the old auto-pick of a $40
 * classic haircut. `duration` is the 15-min squeeze slot. The urgency emergency FEE is added on
 * top of this base price by each surface. Edit here, re-run scripts/sync-shared.sh.
 */
export const EMERGENCY_SERVICE = {
  id: 'emergency-haircut',
  name: 'Emergency Haircut',
  price: 50,
  duration: 15,
  category: 'Haircuts',
} as const;

// ── Client protection (PROTECT-BY-DEFAULT) ──────────────────────────────────────────

/** Normalize the two historical override shapes into a name→allowed map. */
function overrideMap(client: EmergencyClient | null | undefined): Record<string, boolean> {
  const raw = client?.emergencyServiceOverrides;
  if (!raw) return {};
  if (Array.isArray(raw)) {
    const out: Record<string, boolean> = {};
    for (const o of raw) {
      const key = (o.serviceName || o.serviceVariationId || '').toLowerCase().trim();
      if (key) out[key] = !!o.allowEmergencySqueeze;
    }
    return out;
  }
  // Legacy object: { "service name": boolean }
  const out: Record<string, boolean> = {};
  for (const k of Object.keys(raw)) out[k.toLowerCase().trim()] = !!(raw as any)[k];
  return out;
}

/**
 * MAY an emergency be squeezed around this client's given service?
 * DEFAULT-ALLOW: returns true unless the client has toggled this service OFF (a protect
 * override with allowEmergencySqueeze:false) or set a global blockSqueezing.
 */
export function clientServiceEmergencyAllowed(
  client: EmergencyClient | null | undefined,
  serviceName: string | null | undefined,
): boolean {
  if (client?.emergencyBookingSettings?.blockSqueezing) return false;   // whole client protected
  const key = (serviceName || '').toLowerCase().trim();
  if (!key) return true;                         // unknown service → default allow
  const map = overrideMap(client);
  return map[key] !== false;                     // protected only if an explicit false override exists
}

/**
 * Is a specific booked appointment protected from having an emergency squeezed next to it?
 * Protected only if the client toggled that service off (or an appointment-level
 * noEmergencySqueeze). Default = NOT protected (squeezing allowed).
 */
export function isAppointmentSqueezeProtected(
  appt: EmergencyAppointment | null | undefined,
  client: EmergencyClient | null | undefined,
): boolean {
  if (appt?.noEmergencySqueeze) return true;
  return !clientServiceEmergencyAllowed(client, appt?.service);
}

/**
 * MAY this client BOOK an emergency squeeze-in for THIS service? (Jose 2026-10-02, task #46 Stage 3.)
 * The flip side of the protect rule above. RESTRICTED services (service.allowEmergencySqueeze === false —
 * e.g. fades/shaves, set per-service in Service Management) can normally NOT be emergency-squeezed; only a
 * client who's been GRANTED access for that service (a client override with allowEmergencySqueeze:true)
 * may book one. Non-restricted services are open to anyone. Separate from the "protect around an existing
 * appointment" rule (that still wins). A restricted service with no grant → denied (safe default).
 */
export function clientMayEmergencyBookService(
  client: EmergencyClient | null | undefined,
  service: { name?: string; id?: string; allowEmergencySqueeze?: boolean } | null | undefined,
): boolean {
  if (!service || service.allowEmergencySqueeze !== false) return true;   // not restricted → open to all
  const key = (service.name || service.id || '').toLowerCase().trim();
  if (!key) return false;                                                  // restricted + unknown → deny
  return overrideMap(client)[key] === true;                               // restricted → needs a GRANT
}

// ── Barber BPI + eligibility (PERFORMANCE GATE + MASTER SWITCH) ──────────────────────

export type EmergencyAccessState =
  | 'active'      // master switch ON — barber can take emergencies now
  | 'eligible'    // meets ALL PPI privilege requirements, not yet granted (admin can flip on)
  | 'requested'   // barber asked; awaiting eligibility/grant
  | 'locked';     // some requirement unmet and not manually overridden

export interface BarberEmergencyEligibility {
  state: EmergencyAccessState;
  active: boolean;                 // convenience: state === 'active'
  ppi: number;                     // composite Professional Practice Index (context, not the gate)
  eligible: boolean;               // all privilege requirements met (or manual override)
  requested: boolean;
  requirements: PrivilegeRequirement[];   // the specific gate: technical/habits/attendance/comm/retention
  unmet: PrivilegeRequirement[];
  reasons: string[];               // human-readable gating explanation
}

/**
 * The full barber emergency picture. The PRIVILEGE is gated on specific PPI pillars, not one
 * number (see shared/ppi.ts::emergencyPrivilegeCheck) — emergency bookings represent the shop,
 * so it's about TRUST. `shopAvgRetention` is the shop-average client-retention the barber's
 * retention is compared against. Master switch (emergencyBooking.enabled) is authoritative for
 * runtime; eligibility governs whether the switch may be flipped on (unless manualOverride).
 */
export function barberEmergencyEligibility(
  barber: EmergencyBarber | null | undefined,
  shopAvgRetention: number = 50,
): BarberEmergencyEligibility {
  const eb = barber?.emergencyBooking || {};
  const priv = emergencyPrivilegeCheck(barber?.ppiScores, shopAvgRetention);
  const { ppi } = computePPI(barber?.ppiScores);
  const manual = !!eb.manualOverride;
  const eligible = manual || priv.eligible;
  const requested = !!eb.requested;
  const active = eb.enabled === true;

  let state: EmergencyAccessState;
  const reasons: string[] = [];
  if (active) {
    state = 'active';
    reasons.push(manual ? 'Access granted (manual override).' : 'Access granted — meets emergency privilege requirements.');
  } else if (eligible) {
    state = 'eligible';
    reasons.push(manual ? 'Eligible via manual override — awaiting grant.' : 'Meets all requirements — awaiting admin grant.');
  } else if (requested) {
    state = 'requested';
    reasons.push(`Requested — still needs: ${priv.unmet.map(r => r.label).join(', ')}.`);
  } else {
    state = 'locked';
    reasons.push(`Locked — needs: ${priv.unmet.map(r => `${r.label} ${r.need}`).join(', ')}. Barber can request access.`);
  }

  return { state, active, ppi, eligible, requested, requirements: priv.requirements, unmet: priv.unmet, reasons };
}

/** Runtime shortcut: can this barber currently accept emergency bookings? (master switch). */
export function canBarberTakeEmergency(barber: EmergencyBarber | null | undefined): boolean {
  return barber?.emergencyBooking?.enabled === true;
}

/**
 * THE one source of truth for "which barbers may take emergencies right now" — every surface
 * (POS Operations emergency tile + customer-portal emergency finder) filters through this so they
 * can never disagree. Eligibility is the admin's MANUAL master switch only (emergencyBooking.enabled).
 * Auto-unlock (BPI / default-on) is intentionally NOT here yet — we collect data before automating it
 * (Jose 2026-08-17). When that day comes, change this ONE function and all surfaces follow.
 */
export function emergencyEligibleBarbers<T extends EmergencyBarber>(barbers: T[] | null | undefined): T[] {
  return (barbers || []).filter(b => canBarberTakeEmergency(b));
}

/** May the admin flip the master switch ON for this barber right now? */
export function canGrantEmergencyAccess(
  barber: EmergencyBarber | null | undefined,
  shopAvgRetention: number = 50,
): boolean {
  return barberEmergencyEligibility(barber, shopAvgRetention).eligible;
}
